import type { DaoConfig } from '@/types/daoConfig';
import { adapterClient } from '@/api/adapter/client';
import { adapterConfig } from '@/api/config';

/**
 * Default DAO configuration and terminology dictionary.
 * Serves as fallback and represents the server's configured DAO (SPEC-0012).
 */
export const DEFAULT_DAO_CONFIG: DaoConfig = {
  id: 'software',
  name: 'Abako',
  tagline: 'Decentralized Marketplace for Freelancers',
  description: 'Marketplace built around accountability for Web3 and software engineering teams.',
  sector: 'software',
  currency: {
    symbol: 'KVN',
    name: 'Kreivo Native',
    decimals: 0,
  },
  projectTypes: [
    'Smart Contract',
    'Frontend',
    'Full Stack',
    'Mobile App',
    'MVP',
    'Audit',
    'DevOps & Infrastructure',
    'Other',
  ],
  terminology: {
    workerRole: 'Developer',
    workerRolePlural: 'Developers',
    clientRole: 'Client',
    coordinatorRole: 'Coordinator',
    workerPitch: "I'm looking to work in Web3 & software projects",
    clientPitch:
      "I'm looking for specialized talent. I need consultants, project leaders, and developers.",
    externalProfile: {
      id: 'github',
      label: 'GitHub username (optional)',
      placeholder: 'yourusername',
      icon: 'ri-github-line',
      helpText: 'Public GitHub profile to showcase your code and contributions.',
    },
    skillsHelperText:
      'Include programming languages, frameworks, libraries, tools, or soft skills relevant to software engineering.',
    backgroundHelperText:
      'Tell us about your experience, engineering journey, education, and notable software projects.',
    emptyStateWorkerText:
      'Projects will appear here once you are assigned as a consultant or developer.',
    emptyStateClientText:
      'Create your first project proposal to get started with Abako.',
  },
};

/**
 * Candidate platform profile endpoints in order of priority:
 * 1. /platform (canonical endpoint defined in SPEC-0012)
 * 2. /dao/info (alias / backend info candidate)
 * 3. /dao/config (fallback alias)
 */
const CANDIDATE_PLATFORM_ENDPOINTS = [
  adapterConfig.endpoints.platform.profile,
  adapterConfig.endpoints.platform.daoInfo,
  '/dao/config',
] as const;

/**
 * Fetches the active DAO configuration and dynamic terminology dictionary from the server.
 * Queries /platform (SPEC-0012) via adapterClient, trying aliases /dao/info and /dao/config,
 * with graceful fallback to DEFAULT_DAO_CONFIG when offline or in test environments.
 */
export async function fetchDaoConfig(): Promise<DaoConfig> {
  for (const endpoint of CANDIDATE_PLATFORM_ENDPOINTS) {
    try {
      const response = await adapterClient.get<DaoConfig>(endpoint);
      if (response?.data && typeof response.data === 'object' && response.data.name) {
        return response.data;
      }
    } catch {
      // Try next candidate endpoint
    }
  }

  // Direct fetch fallback for environments where adapterClient baseURL might differ
  try {
    const res = await fetch('/api/platform');
    if (res.ok) {
      const data = (await res.json()) as DaoConfig;
      if (data && typeof data === 'object' && data.name) {
        return data;
      }
    }
  } catch {
    // Offline or test environment
  }

  return DEFAULT_DAO_CONFIG;
}
